import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { clearAuth } from "../../store/authSlice";

const MOBILE_REGEX = /^\d{10}$/;

function EyeIcon({ open }) {
  return open ? (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
  );
}

export default function MyProfile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [roleName, setRoleName] = useState("");
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    mobile_no: "",
    dob: "",
    password: "",
    confirm_password: "",
  });

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/profile/me");
      const d = res.data?.data;
      if (d) {
        setForm({
          first_name: d.first_name || "",
          last_name: d.last_name || "",
          mobile_no: d.mobile_no || "",
          dob: d.dob || "",
          password: "",
          confirm_password: "",
        });
        setEmail(d.email || "");
        setRoleName(d.role?.name || "");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const validate = () => {
    const next = {};

    if (!form.first_name.trim()) next.first_name = "Name is required";

    if (form.mobile_no && !MOBILE_REGEX.test(form.mobile_no)) {
      next.mobile_no = "Mobile number must be exactly 10 digits";
    }

    if (form.password || form.confirm_password) {
      if (form.password.length < 8) {
        next.password = "Password must be at least 8 characters";
      } else if (form.password !== form.confirm_password) {
        next.confirm_password = "Passwords do not match";
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        mobile_no: form.mobile_no,
        dob: form.dob || null,
      };
      if (form.password) {
        payload.password = form.password;
        payload.confirm_password = form.confirm_password;
      }

      const res = await api.put("/admin/profile/me", payload);
      toast.success(res.data?.message || "Profile updated successfully");

      if (res.data?.password_changed) {
        dispatch(clearAuth());
        navigate("/login", { replace: true });
        return;
      }

      const updatedUser = JSON.parse(localStorage.getItem("admin_user") || "{}");
      const merged = {
        ...updatedUser,
        first_name: form.first_name,
        last_name: form.last_name,
        mobile_no: form.mobile_no,
      };
      localStorage.setItem("admin_user", JSON.stringify(merged));

      setForm((prev) => ({ ...prev, password: "", confirm_password: "" }));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm p-6">Loading...</div>;

  return (
    <div className=" mx-auto">
      <button
        className="text-sm text-blue-600 font-semibold mb-4"
        onClick={() => navigate(-1)}
      >
        {"←"} Back
      </button>

      <div className="bg-white rounded-2xl border p-4 sm:p-8">
        <h1 className="text-xl sm:text-2xl font-bold">My Profile</h1>
        {/* <p className="text-sm text-gray-500 mt-1">
          View and update your account details.
        </p> */}

        <form onSubmit={onSubmit} className="mt-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="text-sm font-semibold block mb-1.5">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => setField("first_name", e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
                placeholder="First name"
              />
              {errors.first_name && (
                <p className="text-xs text-red-500 mt-1">{errors.first_name}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1.5">Last Name</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => setField("last_name", e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
                placeholder="Last name"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="text-sm font-semibold block mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                readOnly
                disabled
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1.5">Role</label>
              <input
                type="text"
                value={roleName}
                readOnly
                disabled
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="text-sm font-semibold block mb-1.5">Mobile Number</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={10}
                value={form.mobile_no}
                onChange={(e) =>
                  setField("mobile_no", e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
                placeholder="10 digit mobile number"
              />
              {errors.mobile_no && (
                <p className="text-xs text-red-500 mt-1">{errors.mobile_no}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1.5">Date of Birth</label>
              <input
                type="date"
                value={form.dob || ""}
                onChange={(e) => setField("dob", e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
              />
            </div>
          </div>

          <div className="border-t pt-5">
            <p className="text-sm font-semibold mb-1">Change Password</p>
            <p className="text-xs text-gray-500 mb-4">
              Leave both fields empty to keep your current password.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-semibold block mb-1.5">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setField("password", e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-4 pr-11 py-2.5 outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
                    placeholder="Enter new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                  >
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-500 mt-1">{errors.password}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-semibold block mb-1.5">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={form.confirm_password}
                    onChange={(e) => setField("confirm_password", e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-4 pr-11 py-2.5 outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
                    placeholder="Re-enter new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                  >
                    <EyeIcon open={showConfirmPassword} />
                  </button>
                </div>
                {errors.confirm_password && (
                  <p className="text-xs text-red-500 mt-1">{errors.confirm_password}</p>
                )}
              </div>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={saving}
              className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
